import { WifiOff, AlertTriangle, RefreshCw } from 'lucide-react-native';

import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';

export type InboxListErrorType = 'connection' | 'unknown';

type InboxListErrorProps = {
  errorType: InboxListErrorType;
  onRetry?: () => void;
};

type ErrorConfig = {
  icon: typeof WifiOff;
  title: string;
  message: string;
};

const errorConfigs: Record<InboxListErrorType, ErrorConfig> = {
  connection: {
    icon: WifiOff,
    title: 'Connection Error',
    message: 'Unable to load your messages. Please check your internet connection and try again.',
  },
  unknown: {
    icon: AlertTriangle,
    title: 'Unable to Load Messages',
    message: 'Something went wrong while loading your messages. Please try again.',
  },
};

export function getInboxListErrorType(error: unknown): InboxListErrorType {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    if (message.includes('network') || message.includes('timeout') || message.includes('fetch')) {
      return 'connection';
    }
  }

  return 'unknown';
}

export function InboxListError({ errorType, onRetry }: InboxListErrorProps) {
  const config = errorConfigs[errorType];

  return (
    <VStack className="flex-1 items-center justify-center px-5 py-8">
      <VStack className="max-w-md items-center" space="lg">
        <Icon as={config.icon} size="xl" className="text-typography-400" />

        <VStack className="items-center" space="sm">
          <Heading size="lg" className="text-center">
            {config.title}
          </Heading>

          <Text className="text-center text-typography-500">{config.message}</Text>
        </VStack>

        {onRetry && (
          <Button variant="outline" onPress={onRetry}>
            <Icon as={RefreshCw} size="sm" className="mr-2" />
            <ButtonText>Try Again</ButtonText>
          </Button>
        )}
      </VStack>
    </VStack>
  );
}
